import { useState, useEffect, useMemo, ReactNode } from 'react';
import { User } from '../domain/entities/User';
import { AuthService } from '../application/services/AuthService';
import { AuthRepository } from '../infrastructure/repositories/AuthRepository';
import { AuthObservable, IAuthObserver } from '../application/observables/AuthObservable';
import { LoggerFactory } from '../application/logging/Logger';
import { ConfigManager } from '../application/config/Config';
import { ValidationError } from '../application/errors/AppError';
import { ValidatorFactory } from '../application/validation/Validator';
import { AuthContext } from './AuthContextDefinition';

/**
 * Auth Context
 * Follows SOLID principles and industry best practices
 */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const logger = LoggerFactory.getLogger();
  const config = ConfigManager.getInstance();
  
  // Memoize objects to prevent unnecessary re-renders
  const authObservable = useMemo(() => new AuthObservable(), []);
  
  // Initialize services
  const authRepository = useMemo(() => new AuthRepository(), []);
  const authService = useMemo(() => new AuthService(authRepository), [authRepository]);

  // Auth Observer Implementation
  const authObserver: IAuthObserver = useMemo(() => ({
    onUserLogin: (user: User) => {
      setUser(user);
      setIsAuthenticated(true);
      setError(null);
      
      // Store user data in localStorage (token should already be stored by the service)
      //localStorage.setItem(config.getAuthConfig().userDataKey, JSON.stringify(user.toJSON()));
    },
    
    onUserLogout: () => {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      
      // Clear localStorage
      localStorage.removeItem(config.getAuthConfig().tokenKey);
   //   localStorage.removeItem(config.getAuthConfig().userDataKey);
      localStorage.clear();
    },
    
    onUserUpdate: (user: User) => {
      setUser(user);
      setIsAuthenticated(true);
   //   localStorage.setItem(config.getAuthConfig().userDataKey, JSON.stringify(user.toJSON()));
    },
    
    onAuthError: (error: string) => {
      setError(error);
    }
  }), [config]);

  // Subscribe to auth observable
  useEffect(() => {
    const unsubscribe = authObservable.subscribe(authObserver);
    return unsubscribe;
  }, [authObservable, authObserver]);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem(config.getAuthConfig().tokenKey);
       // const storedUser = localStorage.getItem(config.getAuthConfig().userDataKey);
        
        if (token) {
          try {
       //     const userData = JSON.parse(storedUser);
           // const user = User.fromData(userData);
            
            // Validate token with API
            const validatedUser = await authService.validateToken(token);
            if (validatedUser) {
              authObservable.setUser(validatedUser);
            } else {
              // If validation fails, use the stored user data
              //authObservable.setUser(user);
            }
          } catch (parseError) {
            logger.error('Invalid stored user data', parseError as Error);
            authObservable.clearUser();
          }
        } else {
          authObservable.clearUser();
        }
      } catch (error) {
        logger.error('Authentication check failed', error as Error);
        authObservable.clearUser();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [authObservable, authService, config, logger]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate inputs
      const emailValidator = ValidatorFactory.createEmailValidator();

      const emailValidation = emailValidator.validate(email);
      if (!emailValidation.isValid) {
        throw new ValidationError(`Email validation failed: ${emailValidation.errors.join(', ')}`);
      }

      // const passwordValidation = passwordValidator.validate(password);
      // if (!passwordValidation.isValid) {
      //   throw new ValidationError(`Password validation failed: ${passwordValidation.errors.join(', ')}`);
      // }

      const result = await authService.login(email, password);
      
      // Store token and user data
      localStorage.setItem(config.getAuthConfig().tokenKey, result.token);
     // localStorage.setItem(config.getAuthConfig().userDataKey, JSON.stringify(result.user.toJSON()));
      
      // Notify observers
      authObservable.setUser(result.user);
      
      logger.info('User login successful', { userId: result.user.id, email: result.user.email });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      authObservable.notifyError(errorMessage);
      logger.error('Login failed', error as Error, { email });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    type: 'swimmer' | 'coach';
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate inputs
      const emailValidator = ValidatorFactory.createEmailValidator();
      const passwordValidator = ValidatorFactory.createPasswordValidator();
      const firstNameValidator = ValidatorFactory.createNameValidator('First Name');
      const lastNameValidator = ValidatorFactory.createNameValidator('Last Name');

      const validations = [
        emailValidator.validate(userData.email),
        passwordValidator.validate(userData.password),
        firstNameValidator.validate(userData.firstName),
        lastNameValidator.validate(userData.lastName)
      ];

      const allErrors = validations.flatMap(v => v.errors);
      if (allErrors.length > 0) {
        throw new ValidationError(`Validation failed: ${allErrors.join(', ')}`);
      }

      const result = await authService.register(userData);
      
      // Store token
      //localStorage.setItem(config.getAuthConfig().tokenKey, result.token);
      if (result.token) {
        localStorage.setItem(config.getAuthConfig().tokenKey, result.token);
      } else {
        logger.warn('Token is null after registration');
      }

      // Notify observers
      authObservable.setUser(result.user);
      
      logger.info('User registration successful', { userId: result.user.id, email: result.user.email });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      authObservable.notifyError(errorMessage);
      logger.error('Registration failed', error as Error, { email: userData.email });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      const token = localStorage.getItem(config.getAuthConfig().tokenKey);
      
      // Call logout API if token exists
      if (token) {
        authService.logout(token).catch(error => {
          // Don't throw error for logout - just log it
          logger.error('Logout API call failed', error as Error);
        });
      }
      
      // Preserve theme preference before clearing storage
      const themePreference = localStorage.getItem('theme');
      
      // Clear all localStorage items
      localStorage.clear();
      
      // Restore theme preference if it existed
      if (themePreference) {
        localStorage.setItem('theme', themePreference);
      }
      
      // Clear all sessionStorage items
      sessionStorage.clear();
      
      // Clear auth observable state
      authObservable.clearUser();
      
      // Reset local state
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsLoading(false);
      
      logger.info('User logout completed - all session data cleared, theme preserved');
    } catch (error) {
      logger.error('Error during logout cleanup', error as Error);
      // Even if there's an error, we should still clear the state
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
  try {
    setIsLoading(true);
    setError(null);

    const emailValidator = ValidatorFactory.createEmailValidator();
    const emailValidation = emailValidator.validate(email);
    if (!emailValidation.isValid) {
      throw new ValidationError(`Email validation failed: ${emailValidation.errors.join(', ')}`);
    }

    const result = await authService.forgotPassword(email); 

    logger.info('Password reset email sent', { email });
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';
    setError(errorMessage);
    authObservable.notifyError(errorMessage);
    logger.error('Password reset request failed', error as Error, { email });
    throw error;
  } finally {
    setIsLoading(false);
  }
};


  const resetPassword = async (email: string, otp: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const emailValidator = ValidatorFactory.createEmailValidator();
 
      const emailValidation = emailValidator.validate(email);
      if (!emailValidation.isValid) {
        throw new ValidationError(`Email validation failed: ${emailValidation.errors.join(', ')}`);
      }

      // const passwordValidation = passwordValidator.validate(newPassword);
      // if (!passwordValidation.isValid) {
      //   throw new ValidationError(`Password validation failed: ${passwordValidation.errors.join(', ')}`);
      // }

      await authService.resetPassword(email, otp, newPassword);
      logger.info('Password reset successful', { email });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      setError(errorMessage);
      authObservable.notifyError(errorMessage);
      logger.error('Password reset failed', error as Error, { email });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!user) {
        throw new ValidationError('User must be logged in to change password');
      }

      const passwordValidator = ValidatorFactory.createPasswordValidator();
      const newPasswordValidation = passwordValidator.validate(newPassword);
      if (!newPasswordValidation.isValid) {
        throw new ValidationError(`New password validation failed: ${newPasswordValidation.errors.join(', ')}`);
      }

      await authService.changePassword(user.id, currentPassword, newPassword);
      logger.info('Password changed successfully', { userId: user.id });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      setError(errorMessage);
      authObservable.notifyError(errorMessage);
      logger.error('Password change failed', error as Error, { userId: user?.id });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      changePassword,
      clearError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

